import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Stable guest ID for unauthenticated visitors — used to enforce the 1-prayer guest limit.
export function getGuestId() {
  let g = localStorage.getItem("inlight_guest_id");
  if (!g) {
    g = (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("inlight_guest_id", g);
  }
  return g;
}

// A fresh client_request_id for each user action that triggers an LLM call.
// Reusing the same ID for a retry is OK — the backend de-dupes via idempotency cache.
export function newClientRequestId() {
  return (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
         Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("inlight_token");
  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Always send the guest ID — backend ignores it for authenticated users.
  config.headers["X-Guest-Id"] = getGuestId();
  return config;
});

// Unverified accounts are gently routed to the verification screen.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (
      error?.response?.status === 403 &&
      detail?.code === "email_unverified" &&
      window.location.pathname !== "/verify-email"
    ) {
      window.location.href = "/verify-email";
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // Our 429 detail is an object with code/message
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// For prayer-generation calls: show a calm message on server/engine errors
// (5xx or non-JSON gateway HTML) instead of surfacing raw infra text.
export function friendlyGenerationError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && (typeof detail === "object" || typeof detail === "string")) {
    return formatApiErrorDetail(detail);
  }
  const status = err?.response?.status;
  if (status && status >= 500) {
    return "We could not reach the prayer engine just now. Please try again in a moment.";
  }
  return err?.message || "Something went wrong. Please try again.";
}

// Extract the structured 429 detail from an axios error (or undefined).
export function getLimitDetail(err) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.code) return d;
  return null;
}
